/**
 * Подписка на оповещения индикатора — общая для всех, кроме зон PRO и
 * дивергенций, у которых свои сторожа со своей историей.
 *
 * Устроена одинаково для любого индикатора: какой индикатор, где и на
 * каком таймфрейме, и слепок его настроек. Что считать событием, знает
 * описание индикатора в `INDICATOR_EVENTS`: сервер на каждой закрытой
 * свече спрашивает его, случилось ли что-то на этой свече, — и если да,
 * будит человека. Новому индикатору оповещения даются описанием его
 * событий, а не новым сторожем.
 *
 * Слепок — все настройки экземпляра как есть: события считаются по тем
 * же порогам, по которым индикатор рисует зоны на графике.
 */
import { isTimeframe, timeframeMs } from '@specter/types';
import { imbalanceZones, orderBlockZones } from './structure.js';
import { ema, emaOfSparse, rollingStdDev, rsi, sma } from './math.js';
import { liquidityMap } from './liquidity.js';
import { volumeProfile } from './profile.js';
import { analyzeDelta, DELTA_WINDOW } from './delta-analysis.js';
import { FIBO_SWINGS, fiboGrids, fiboLevel } from './fibo-check.js';
import { zoneSeries } from './zones.js';
import { pro2OptionsFrom, pro2PlanShown, pro2State } from './zones-pro2.js';
/** Ключ подписки. Собирается в одном месте: его разбирают и терминал, и сервер. */
export function indicatorWatchId(instanceId, venue, symbol, timeframe) {
    return `${instanceId}:${venue}|${symbol}|${timeframe}`;
}
/** Подписки из профиля; непонятная запись пропускается молча. */
export function indicatorWatchesFrom(raw) {
    if (!Array.isArray(raw))
        return [];
    const out = [];
    const seen = new Set();
    for (const entry of raw) {
        const watch = asIndicatorWatch(entry);
        if (!watch || seen.has(watch.id))
            continue;
        seen.add(watch.id);
        out.push(watch);
    }
    return out;
}
function asIndicatorWatch(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const v = value;
    if (typeof v['id'] !== 'string' || typeof v['indicator'] !== 'string')
        return null;
    if (!(v['indicator'] in INDICATOR_EVENTS))
        return null;
    if (typeof v['venue'] !== 'string' || typeof v['symbol'] !== 'string')
        return null;
    if (typeof v['timeframe'] !== 'string' || !isTimeframe(v['timeframe']))
        return null;
    const params = {};
    if (typeof v['params'] === 'object' && v['params'] !== null) {
        for (const [key, item] of Object.entries(v['params'])) {
            if (typeof item === 'number' || typeof item === 'string' || typeof item === 'boolean')
                params[key] = item;
        }
    }
    return {
        id: v['id'],
        indicator: v['indicator'],
        venue: v['venue'],
        symbol: v['symbol'],
        timeframe: v['timeframe'],
        params,
        createdAt: typeof v['createdAt'] === 'number' ? v['createdAt'] : 0,
    };
}
/** Таймфрейм словами: «4ч», «15м» — как на кнопках. */
export function timeframeWords(timeframe) {
    return TF_WORDS[timeframe] ?? timeframe;
}
function num(p, key, fallback) {
    const value = p[key];
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function flag(p, key, fallback) {
    const value = p[key];
    return typeof value === 'boolean' ? value : fallback;
}
/** Цена словами: «2 632,5» — как на табло. */
export function priceText(value) {
    const abs = Math.abs(value);
    const digits = abs >= 1000 ? 1 : abs >= 1 ? 2 : abs >= 0.01 ? 4 : 6;
    return value.toLocaleString('ru-RU', { maximumFractionDigits: digits }).replace(/ /g, ' ');
}
const TF_WORDS = {
    '1m': '1м',
    '3m': '3м',
    '5m': '5м',
    '15m': '15м',
    '30m': '30м',
    '1H': '1ч',
    '2H': '2ч',
    '4H': '4ч',
    '6H': '6ч',
    '12H': '12ч',
    '1D': '1д',
    '3D': '3д',
    '1W': '1н',
    '1M': '1мес',
};
/** Старшие таймфреймы на выбор — те же галочки, что в настройках индикатора. */
const HIGHER = [
    { key: 'tf15m', timeframe: '15m' },
    { key: 'tf1H', timeframe: '1H' },
    { key: 'tf4H', timeframe: '4H' },
    { key: 'tf1D', timeframe: '1D' },
];
function higherFrames(p, timeframe) {
    return HIGHER.filter((entry) => flag(p, entry.key, entry.timeframe === '4H') && timeframeMs(entry.timeframe) > timeframeMs(timeframe)).map((entry) => entry.timeframe);
}
/**
 * Чьи зоны видны на свечах подписки.
 *
 * Свой таймфрейм — если отмечен «своего таймфрейма». Отмеченный
 * таймфрейм, равный таймфрейму подписки, — это тоже свой: «имбалансы
 * 4ч» при уведомлениях по 4ч раньше молчали вовсе, потому что старшим
 * считался только строго старший. Младшие не видны: 4ч зону по дневным
 * свечам не отследить — сообщение пришло бы на сутки позже касания.
 */
function watchedFrames(p, timeframe) {
    const own = flag(p, 'tfCurrent', true) || HIGHER.some((entry) => entry.timeframe === timeframe && flag(p, entry.key, entry.timeframe === '4H'));
    return [...(own ? [timeframe] : []), ...higherFrames(p, timeframe)];
}
/** Свои свечи и отмеченные старшие — откуда брать зоны. */
function sourcesOf(input, p) {
    const out = [];
    for (const frame of watchedFrames(p, input.timeframe)) {
        const bars = frame === input.timeframe ? input.bars : input.frames[frame];
        if (bars && bars.length >= 3)
            out.push({ frame, bars });
    }
    return out;
}
/**
 * Свечи подписки после того, как зона стала известна.
 *
 * Старшая зона известна, когда закрылась её третья свеча, — раньше о
 * ней не знал и график. Считать касания по своим свечам, а не по старшим,
 * нужно затем, чтобы оповещение пришло на той свече, где цена дошла, а
 * не через четыре часа.
 */
function after(bars, formedAt, frame) {
    const known = formedAt + timeframeMs(frame);
    let start = bars.length;
    for (let i = bars.length - 1; i >= 0; i -= 1) {
        if (bars[i].time < known)
            break;
        start = i;
    }
    return bars.slice(start);
}
const IMBALANCES = {
    name: 'Имбалансы',
    about: 'цена вошла в имбаланс или дошла до его середины',
    frames: higherFrames,
    watched: watchedFrames,
    events(input, p) {
        const last = input.bars[input.bars.length - 1];
        if (!last)
            return [];
        const onEnter = flag(p, 'notifyEnter', true);
        const onMid = flag(p, 'notifyMid', true);
        if (!onEnter && !onMid)
            return [];
        const out = [];
        for (const source of sourcesOf(input, p)) {
            const zones = imbalanceZones(source.bars, num(p, 'minSize', 0.25))
                // Та же выборка, что на графике: последние незакрытые — и те, что
                // закрылись только сейчас, на последней старшей свече.
                .filter((zone) => zone.closedAt === null || zone.closedAt >= last.time - timeframeMs(source.frame))
                .slice(-num(p, 'maxAlive', 8) * 2);
            for (const zone of zones) {
                const own = after(input.bars, zone.formedAt, source.frame);
                if (own.length === 0 || own[own.length - 1].time !== last.time)
                    continue;
                const before = own.slice(0, -1).reduce((depth, bar) => Math.max(depth, depthOf(zone, bar)), 0);
                const now = Math.max(before, depthOf(zone, last));
                if (before >= 1)
                    continue;
                const words = `${TF_WORDS[source.frame] ?? source.frame} ${zone.side === 'up' ? 'бычий' : 'медвежий'} имбаланс`;
                const span = `${priceText(zone.lower)} – ${priceText(zone.upper)}`;
                const side = zone.side === 'up' ? 'LONG' : 'SHORT';
                const id = `imb-${source.frame}-${zone.side}-${zone.formedAt}`;
                // Дошла до середины — сильнее, чем «вошла»: одной свечой бывает и то
                // и другое, и оповещение одно, о большем.
                if (onMid && before < 0.5 && now >= 0.5) {
                    out.push({
                        key: `${id}-mid`,
                        side,
                        text: `Цена дошла до середины: ${words} ${span}, середина ${priceText((zone.upper + zone.lower) / 2)}.`,
                    });
                }
                else if (onEnter && before <= 0 && now > 0) {
                    out.push({ key: `${id}-enter`, side, text: `Цена вошла в ${words} ${span}.` });
                }
            }
        }
        return out;
    },
};
/** Насколько глубоко свеча зашла в имбаланс, 0…1. */
function depthOf(zone, bar) {
    const height = zone.upper - zone.lower;
    if (height <= 0)
        return 0;
    const depth = zone.side === 'up' ? zone.upper - bar.low : bar.high - zone.lower;
    return Math.max(0, Math.min(1, depth / height));
}
function touchOrdinal(count) {
    const words = ['первое', 'второе', 'третье', 'четвёртое', 'пятое'];
    return words[count - 1] ?? `${count}-е`;
}
const ORDER_BLOCKS = {
    name: 'Ордерблоки',
    about: 'цена вернулась в ордерблок или пробила его',
    frames: higherFrames,
    watched: watchedFrames,
    events(input, p) {
        const last = input.bars[input.bars.length - 1];
        if (!last)
            return [];
        const onTouch = flag(p, 'notifyTouch', true);
        const onBreak = flag(p, 'notifyBreak', true);
        if (!onTouch && !onBreak)
            return [];
        const out = [];
        for (const source of sourcesOf(input, p)) {
            const blocks = orderBlockZones(source.bars, num(p, 'strength', 1.5))
                .filter((block) => block.brokenAt === null || block.brokenAt >= last.time - timeframeMs(source.frame))
                .slice(-num(p, 'maxAlive', 6) * 2);
            for (const block of blocks) {
                const own = after(input.bars, block.formedAt, source.frame);
                if (own.length === 0 || own[own.length - 1].time !== last.time)
                    continue;
                // Та же хронология, что у `orderBlockZones`: сначала надо выйти из
                // зоны, чтобы возвращение считалось касанием.
                let inside = true;
                let touches = 0;
                let broken = false;
                for (const bar of own.slice(0, -1)) {
                    const enters = entersBlock(block, bar);
                    if (enters && !inside)
                        touches += 1;
                    inside = enters;
                    if (breaksBlock(block, bar)) {
                        broken = true;
                        break;
                    }
                }
                if (broken)
                    continue;
                const words = `${TF_WORDS[source.frame] ?? source.frame} ${block.side === 'up' ? 'бычий' : 'медвежий'} ордерблок`;
                const span = `${priceText(block.lower)} – ${priceText(block.upper)}`;
                const id = `ob-${source.frame}-${block.side}-${block.start}`;
                if (breaksBlock(block, last)) {
                    if (onBreak) {
                        const beyond = block.side === 'up' ? 'ниже' : 'выше';
                        /*
                         * Старший блок младшей свечой — ещё не пробой по графику.
                         *
                         * На графике 4ч блок гаснет, когда за ним закрылась 4ч
                         * свеча. Сообщение приходит раньше, по свече подписки, — и
                         * должно сказать это прямо, иначе лента говорит «пробит», а
                         * график ещё показывает блок.
                         */
                        const early = source.frame !== input.timeframe;
                        out.push({
                            key: `${id}-break`,
                            // Пробитая опора — повод к продаже, пробитое сопротивление — к покупке.
                            side: block.side === 'up' ? 'SHORT' : 'LONG',
                            text: early
                                ? `${words[0].toUpperCase()}${words.slice(1)} ${span} пробивается: свеча ${timeframeWords(input.timeframe)} закрылась ${beyond} зоны. Пробой подтвердится, если ${beyond} закроется и свеча ${timeframeWords(source.frame)}.`
                                : `${words[0].toUpperCase()}${words.slice(1)} ${span} пробит: свеча закрылась ${beyond} зоны.`,
                        });
                    }
                    continue;
                }
                if (onTouch && entersBlock(block, last) && !inside) {
                    out.push({
                        key: `${id}-touch-${touches + 1}`,
                        side: block.side === 'up' ? 'LONG' : 'SHORT',
                        text: `Цена вернулась в ${words} ${span} — ${touchOrdinal(touches + 1)} касание.`,
                    });
                }
            }
        }
        return out;
    },
};
function entersBlock(block, bar) {
    return block.side === 'up' ? bar.low <= block.upper : bar.high >= block.lower;
}
function breaksBlock(block, bar) {
    return block.side === 'up' ? bar.close < block.lower : bar.close > block.upper;
}
/* ------------------------------------------------------- классика */
const none = () => [];
function sel(p, key, fallback) {
    const value = p[key];
    return typeof value === 'string' ? value : fallback;
}
/** Цена свечи по источнику — как `priceOf` в терминале. */
function priceOf(bar, source) {
    switch (source) {
        case 'open':
            return bar.open;
        case 'high':
            return bar.high;
        case 'low':
            return bar.low;
        case 'hl2':
            return (bar.high + bar.low) / 2;
        case 'ohlc4':
            return (bar.open + bar.high + bar.low + bar.close) / 4;
        default:
            return bar.close;
    }
}
/**
 * Пересечение на последней свече — тем же правилом, что отметки на
 * графике (`crossesOf` в терминале): не раньше чем через `gap` свечей
 * после предыдущего.
 */
function crossAtLast(a, b, gap) {
    let lastAt = -Infinity;
    let found = null;
    const end = a.length - 1;
    for (let i = 1; i <= end; i += 1) {
        const a0 = a[i - 1];
        const b0 = b[i - 1];
        const a1 = a[i];
        const b1 = b[i];
        if (a0 == null || b0 == null || a1 == null || b1 == null)
            continue;
        const up = a0 <= b0 && a1 > b1;
        const down = a0 >= b0 && a1 < b1;
        if (!up && !down)
            continue;
        if (i - lastAt < gap)
            continue;
        lastAt = i;
        if (i === end)
            found = up ? 'up' : 'down';
    }
    return found;
}
/** Цена закрытия пересекла линию на последней свече. */
function closeCross(bars, line) {
    const n = bars.length;
    if (n < 2)
        return null;
    const l0 = line[n - 2];
    const l1 = line[n - 1];
    if (l0 == null || l1 == null)
        return null;
    const c0 = bars[n - 2].close;
    const c1 = bars[n - 1].close;
    if (c0 <= l0 && c1 > l1)
        return 'up';
    if (c0 >= l0 && c1 < l1)
        return 'down';
    return null;
}
function lastKey(bars, name) {
    return `${name}-${bars[bars.length - 1].time}`;
}
function average(name, kind, period) {
    return {
        name,
        about: `цена закрылась выше или ниже линии ${name}`,
        frames: none,
        events({ bars }, p) {
            const length = num(p, 'period', period);
            const values = bars.map((bar) => priceOf(bar, sel(p, 'source', 'close')));
            const line = (kind === 'ema' ? ema : sma)(values, length);
            const cross = closeCross(bars, line);
            if (!cross)
                return [];
            const level = priceText(line[line.length - 1]);
            return [
                {
                    key: lastKey(bars, `${kind}${length}-${cross}`),
                    side: cross === 'up' ? 'LONG' : 'SHORT',
                    text: cross === 'up'
                        ? `Цена закрылась выше ${name} (${length}) — ${level}. Линия из сопротивления может стать опорой.`
                        : `Цена закрылась ниже ${name} (${length}) — ${level}. Линия из опоры может стать сопротивлением.`,
                },
            ];
        },
    };
}
const TRIPLE = {
    name: '3 MA',
    about: 'короткая средняя пересекла длинную',
    frames: none,
    events({ bars }, p) {
        const kind = sel(p, 'kind', 'sma') === 'ema' ? ema : sma;
        const values = bars.map((bar) => priceOf(bar, sel(p, 'source', 'close')));
        const fast = kind(values, num(p, 'fast', 5));
        const slow = kind(values, num(p, 'slow', 30));
        // Тот же разрыв, что у отметок: пять свечей между пересечениями.
        const cross = crossAtLast(fast, slow, 5);
        if (!cross)
            return [];
        return [
            {
                key: lastKey(bars, `ma3-${cross}`),
                side: cross === 'up' ? 'LONG' : 'SHORT',
                text: cross === 'up'
                    ? 'Короткая средняя пересекла длинную вверх — рост может начаться. Во флэте такие пересечения часто ложные.'
                    : 'Короткая средняя пересекла длинную вниз — снижение может начаться. Во флэте такие пересечения часто ложные.',
            },
        ];
    },
};
const BOLLINGER = {
    name: 'Bollinger Bands',
    about: 'свеча закрылась за верхней или нижней полосой',
    frames: none,
    events({ bars }, p) {
        const period = num(p, 'period', 20);
        const mult = num(p, 'mult', 2);
        const values = bars.map((bar) => priceOf(bar, sel(p, 'source', 'close')));
        const middle = sma(values, period);
        const deviation = rollingStdDev(values, period);
        const band = (sign) => middle.map((m, i) => {
            const d = deviation[i];
            return m === null || d == null ? null : m + sign * mult * d;
        });
        const out = [];
        if (closeCross(bars, band(1)) === 'up') {
            out.push({
                key: lastKey(bars, 'bb-upper'),
                side: null,
                text: 'Свеча закрылась над верхней полосой Боллинджера — движение сильное, но цена ушла далеко от средней и может откатиться.',
            });
        }
        if (closeCross(bars, band(-1)) === 'down') {
            out.push({
                key: lastKey(bars, 'bb-lower'),
                side: null,
                text: 'Свеча закрылась под нижней полосой Боллинджера — давление сильное, но цена ушла далеко от средней и может отскочить.',
            });
        }
        return out;
    },
};
const RSI = {
    name: 'RSI',
    about: 'RSI вошёл в крайнюю зону или вернулся из неё',
    frames: none,
    events({ bars }, p) {
        const values = rsi(bars.map((bar) => bar.close), num(p, 'period', 14));
        const now = values[values.length - 1];
        const before = values[values.length - 2];
        if (now == null || before == null)
            return [];
        const at = Math.round(now);
        const key = (name) => lastKey(bars, `rsi-${name}`);
        if (before < 70 && now >= 70) {
            return [{ key: key('ob-in'), side: null, text: `RSI вошёл в перекупленность — ${at}. Рост разогнался: дальше он или продолжится, или выдохнется.` }];
        }
        if (before >= 70 && now < 70) {
            return [{ key: key('ob-out'), side: 'SHORT', text: `RSI вернулся из перекупленности — ${at}. Резкий рост выдохся.` }];
        }
        if (before > 30 && now <= 30) {
            return [{ key: key('os-in'), side: null, text: `RSI вошёл в перепроданность — ${at}. Падение разогналось: дальше оно или продолжится, или выдохнется.` }];
        }
        if (before <= 30 && now > 30) {
            return [{ key: key('os-out'), side: 'LONG', text: `RSI вернулся из перепроданности — ${at}. Резкое падение выдохлось.` }];
        }
        return [];
    },
};
const MACD = {
    name: 'MACD',
    about: 'MACD пересёк сигнальную линию или ноль',
    frames: none,
    events({ bars }, p) {
        const closes = bars.map((bar) => bar.close);
        const fast = ema(closes, num(p, 'fast', 12));
        const slow = ema(closes, num(p, 'slow', 26));
        const line = fast.map((f, i) => {
            const s = slow[i];
            return f === null || s == null ? null : f - s;
        });
        const signal = emaOfSparse(line, num(p, 'signal', 9));
        const out = [];
        const cross = crossAtLast(line, signal, 3);
        const level = line[line.length - 1];
        if (cross && level != null) {
            // Против общего тренда — сильный: вверх под нулём, вниз над нулём.
            const strong = cross === 'up' ? level < 0 : level > 0;
            out.push({
                key: lastKey(bars, `macd-${cross}`),
                side: cross === 'up' ? 'LONG' : 'SHORT',
                text: `MACD пересёк сигнальную ${cross === 'up' ? 'вверх' : 'вниз'}${strong
                    ? ` ${cross === 'up' ? 'под' : 'над'} нулём — разворот против тренда, такой стоит внимания`
                    : ' по тренду — откат закончился'}.`,
            });
        }
        const zero = crossAtLast(line, line.map((value) => (value === null ? null : 0)), 1);
        if (zero) {
            out.push({
                key: lastKey(bars, `macd-zero-${zero}`),
                side: zero === 'up' ? 'LONG' : 'SHORT',
                text: zero === 'up'
                    ? 'MACD перешёл выше нуля — короткая средняя обогнала длинную, перевес у роста.'
                    : 'MACD перешёл ниже нуля — короткая средняя ушла под длинную, перевес у снижения.',
            });
        }
        return out;
    },
};
/* ------------------------------------------------- уровни и объём */
const LIQUIDITY = {
    name: 'Карта ликвидности',
    about: 'цена сняла уровень ликвидности',
    frames: none,
    events({ bars }, p) {
        const last = bars[bars.length - 1];
        if (!last)
            return [];
        const taken = liquidityMap(bars, { levels: num(p, 'levels', 400), age: num(p, 'age', 2500) }).filter((level) => level.to === last.time && (level.evictedAt === null || level.evictedAt >= last.time));
        const out = [];
        for (const side of ['above', 'below']) {
            const own = taken.filter((level) => level.side === side);
            if (own.length === 0)
                continue;
            const prices = own.map((level) => level.price);
            const far = side === 'above' ? Math.max(...prices) : Math.min(...prices);
            const what = own.length > 1 ? `уровней: ${own.length}, дальний — ${priceText(far)}` : `уровень ${priceText(far)}`;
            out.push({
                key: lastKey(bars, `liq-${side}`),
                side: null,
                text: side === 'above'
                    ? `Цена сняла ликвидность сверху, ${what}. Стопы собраны — дальше цена часто или разворачивается, или идёт к следующему скоплению.`
                    : `Цена сняла ликвидность снизу, ${what}. Стопы собраны — дальше цена часто или разворачивается, или идёт к следующему скоплению.`,
            });
        }
        return out;
    },
};
const PROFILE = {
    name: 'Профиль объёма',
    about: 'цена пересекла уровень наибольшего объёма',
    frames: none,
    events({ bars }, p) {
        if (bars.length < 3)
            return [];
        // Уровень — каким он был до этой свечи: иначе он ехал бы вслед за ценой.
        const bands = volumeProfile(bars.slice(0, -1), {
            window: num(p, 'window', 300),
            bins: num(p, 'bins', 48),
            threshold: 0,
        });
        let peak = null;
        for (const band of bands)
            if (!peak || band.share > peak.share)
                peak = band;
        if (!peak)
            return [];
        const poc = (peak.low + peak.high) / 2;
        const c0 = bars[bars.length - 2].close;
        const c1 = bars[bars.length - 1].close;
        const up = c0 <= poc && c1 > poc;
        const down = c0 >= poc && c1 < poc;
        if (!up && !down)
            return [];
        return [
            {
                key: lastKey(bars, `poc-${up ? 'up' : 'down'}`),
                side: up ? 'LONG' : 'SHORT',
                text: `Цена закрылась ${up ? 'выше' : 'ниже'} уровня наибольшего объёма — ${priceText(poc)}. Там наторговали больше всего, и он часто становится ${up ? 'опорой' : 'сопротивлением'}.`,
            },
        ];
    },
};
/* ------------------------------------------------- потоки и толпа */
/** Значения ряда по свечам: точное совпадение времени, иначе пусто. */
function byBar(bars, points) {
    const map = new Map((points ?? []).map((point) => [point.at, point.value]));
    return bars.map((bar) => map.get(bar.time) ?? null);
}
/** Расхождения цены с перевесом — тем же разбором, что ставит отметки на графике. */
function flowEvents(name, bars, delta) {
    const last = bars[bars.length - 1];
    if (!last || delta[delta.length - 1] == null)
        return [];
    const found = analyzeDelta(bars, delta, 'all', DELTA_WINDOW, true).divergences.filter((item) => item.time === last.time);
    return found.map((item) => ({
        key: lastKey(bars, `${name}-${item.kind}`),
        side: item.kind === 'buyers-fading' ? 'SHORT' : 'LONG',
        text: item.kind === 'buyers-fading'
            ? 'Цена обновила максимум, а покупок по рынку не прибавилось — рост идёт без поддержки и может выдохнуться.'
            : 'Цена обновила минимум, а продаж по рынку не прибавилось — падение идёт без поддержки и может выдохнуться.',
    }));
}
function cvd(name, kind) {
    return {
        name,
        about: 'цена разошлась с перевесом покупок',
        frames: none,
        data: () => [kind],
        events: ({ bars, series }) => flowEvents(kind, bars, byBar(bars, series?.[kind])),
    };
}
function flowKinds(p) {
    const source = sel(p, 'source', 'all');
    return source === 'binance' ? ['flowBinance'] : source === 'bybit' ? ['flowBybit'] : ['flowBinance', 'flowBybit'];
}
const ORDER_FLOW_DELTA = {
    name: 'Дельта по сделкам',
    about: 'цена разошлась с перевесом сделок',
    frames: none,
    data: flowKinds,
    events({ bars, series }, p) {
        const parts = flowKinds(p).map((kind) => byBar(bars, series?.[kind]));
        const delta = bars.map((_, i) => {
            let sum = null;
            for (const part of parts) {
                const value = part[i];
                if (value != null)
                    sum = (sum ?? 0) + value;
            }
            return sum;
        });
        return flowEvents('flow', bars, delta);
    },
};
function money(value) {
    const abs = Math.abs(value);
    const short = (x) => x.toLocaleString('ru-RU', { maximumFractionDigits: 1 });
    if (abs >= 1e9)
        return `$${short(abs / 1e9)} млрд`;
    if (abs >= 1e6)
        return `$${short(abs / 1e6)} млн`;
    if (abs >= 1e3)
        return `$${short(abs / 1e3)} тыс.`;
    return `$${Math.round(abs)}`;
}
const LIQUIDATIONS = {
    name: 'Ликвидации рынка',
    about: 'случился всплеск ликвидаций',
    frames: none,
    data: () => ['liqLong', 'liqShort'],
    events({ bars, series }, p) {
        const n = bars.length;
        if (n < 30)
            return [];
        const multiple = num(p, 'spike', 3);
        const out = [];
        for (const side of ['long', 'short']) {
            const values = byBar(bars, side === 'long' ? series?.liqLong : series?.liqShort);
            const now = values[n - 1];
            if (now == null || now <= 0)
                continue;
            // Обычное — среднее за полсотни свечей до этой, там, где ликвидации были.
            const past = values.slice(Math.max(0, n - 51), n - 1).filter((value) => value != null && value > 0);
            if (past.length < 10)
                continue;
            const usual = past.reduce((sum, value) => sum + value, 0) / past.length;
            if (usual <= 0 || now < usual * multiple)
                continue;
            const times = (now / usual).toLocaleString('ru-RU', { maximumFractionDigits: 1 });
            out.push({
                key: lastKey(bars, `liq-${side}`),
                side: null,
                text: side === 'long'
                    ? `Всплеск ликвидаций: вынесло лонгов на ${money(now)} — в ${times} раза больше обычного. Покупателей выбило вниз; после такой зачистки падение часто выдыхается.`
                    : `Всплеск ликвидаций: вынесло шортов на ${money(now)} — в ${times} раза больше обычного. Продавцов выбило вверх; после такой зачистки рост часто выдыхается.`,
            });
        }
        return out;
    },
};
const CROWD = {
    name: 'Позиционирование толпы',
    about: 'толпа ушла в крайность — в лонги или в шорты',
    frames: none,
    data: () => ['longAccounts'],
    events({ bars, series }, p) {
        const points = series?.longAccounts ?? [];
        const last = bars[bars.length - 1];
        const prev = bars[bars.length - 2];
        if (!last || !prev || points.length < 2)
            return [];
        // Позиционирование пишется реже свечей: берётся последнее известное к бару.
        const asOf = (time) => {
            let value = null;
            for (const point of points) {
                if (point.at > time)
                    break;
                value = point.value;
            }
            return value;
        };
        const now = asOf(last.time);
        const before = asOf(prev.time);
        if (now === null || before === null)
            return [];
        const high = num(p, 'high', 65) / 100;
        const share = Math.round(now * 100);
        if (before < high && now >= high) {
            return [
                {
                    key: lastKey(bars, 'crowd-long'),
                    side: null,
                    text: `Толпа ушла в лонги: ${share}% счетов ставят на рост. На крайностях толпа часто ошибается — цена нередко идёт против неё.`,
                },
            ];
        }
        if (before > 1 - high && now <= 1 - high) {
            return [
                {
                    key: lastKey(bars, 'crowd-short'),
                    side: null,
                    text: `Толпа ушла в шорты: ${100 - share}% счетов ставят на падение. На крайностях толпа часто ошибается — цена нередко идёт против неё.`,
                },
            ];
        }
        return [];
    },
};
/**
 * Сессии: пробой азиатского диапазона — первый за день.
 *
 * Азия 00:00–08:00 UTC, как на графике. Событие — закрытие свечи за
 * границей диапазона после конца азиатской сессии, один раз в день на
 * каждую сторону: дальше цена может гулять туда-обратно, и каждое
 * возвращение будить незачем.
 */
const SESSIONS = {
    name: 'Сессии',
    about: 'цена закрылась выше или ниже диапазона азиатской сессии',
    frames: none,
    events({ bars, timeframe }, p) {
        if (timeframeMs(timeframe) > 3_600_000)
            return [];
        const last = bars[bars.length - 1];
        if (!last)
            return [];
        const DAY = 86_400_000;
        const day = Math.floor(last.time / DAY) * DAY;
        /*
         * Азия — по Токио, 09:00–18:00 местного, то есть 00:00–09:00 UTC
         * круглый год: летнего времени в Японии нет. Те же часы, что у
         * индикатора на графике и у сессий на «Главной».
         *
         * В субботу и воскресенье Токио закрыт, азиатского диапазона нет —
         * и пробивать нечего. Раньше сторож считал «Азией» ночь выходного и
         * будил пробоем того, чего не было.
         */
        const weekday = new Date(day).getUTCDay();
        if (weekday === 0 || weekday === 6)
            return [];
        // Те же дни недели, что выбраны в индикаторе: не размечен — не будит.
        const chosen = typeof p['weekdays'] === 'string' ? p['weekdays'] : '12345';
        if (!chosen.includes(String(weekday)))
            return [];
        const asiaEnd = day + 9 * 3_600_000;
        if (last.time < asiaEnd)
            return [];
        let high = Number.NEGATIVE_INFINITY;
        let low = Number.POSITIVE_INFINITY;
        for (const bar of bars) {
            if (bar.time < day || bar.time >= asiaEnd)
                continue;
            high = Math.max(high, bar.high);
            low = Math.min(low, bar.low);
        }
        if (!Number.isFinite(high))
            return [];
        // Первый пробой за день: до этой свечи после Азии никто не закрывался за границей.
        const after = bars.filter((bar) => bar.time >= asiaEnd && bar.time < last.time);
        const out = [];
        if (last.close > high && !after.some((bar) => bar.close > high)) {
            out.push({
                key: `sessions-up-${day}`,
                side: 'LONG',
                text: `Цена закрылась выше диапазона азиатской сессии (${priceText(high)}). Удержится — пробой настоящий; вернётся внутрь — был вынос стопов.`,
            });
        }
        if (last.close < low && !after.some((bar) => bar.close < low)) {
            out.push({
                key: `sessions-down-${day}`,
                side: 'SHORT',
                text: `Цена закрылась ниже диапазона азиатской сессии (${priceText(low)}). Удержится — пробой настоящий; вернётся внутрь — был вынос стопов.`,
            });
        }
        return out;
    },
};
/**
 * «Проверка фибо»: пробит диапазон — цель 1.414; цена дошла до 1.414 —
 * зона разворота; от 1.414 вернулась к 1.0. Те же сетки, что на графике.
 */
const FIBO_CHECK = {
    name: 'Проверка фибо',
    about: 'пробит хай или лой диапазона, цена дошла до 1.414 или вернулась от неё к 1.0',
    frames: none,
    events({ bars }, p) {
        const last = bars[bars.length - 1];
        if (!last)
            return [];
        const swingKey = typeof p['swing'] === 'string' && p['swing'] in FIBO_SWINGS ? p['swing'] : 'medium';
        const grids = fiboGrids(bars, FIBO_SWINGS[swingKey]);
        const out = [];
        for (const grid of grids) {
            if (!grid.broken)
                continue;
            const up = grid.broken.side > 0;
            const span = `${priceText(grid.low.price)} – ${priceText(grid.high.price)}`;
            const id = `fibo-${grid.high.time}-${grid.low.time}`;
            if (flag(p, 'notifyBreak', true) && grid.broken.at === last.time) {
                out.push({
                    key: `${id}-break`,
                    side: up ? 'LONG' : 'SHORT',
                    text: `Пробит ${up ? 'хай' : 'лой'} диапазона ${span}: свеча закрылась ${up ? 'выше' : 'ниже'} ${priceText(up ? grid.high.price : grid.low.price)}. Цель по сетке — 1.414 = ${priceText(fiboLevel(grid, 1.414))}.`,
                });
            }
            if (flag(p, 'notifyZone', true) && grid.outcome === 'hit' && grid.outcomeAt === last.time) {
                out.push({
                    key: `${id}-zone`,
                    side: null,
                    text: `Цена дошла до 1.414 (${priceText(fiboLevel(grid, 1.414))}) после пробоя диапазона ${span}. Зона разворота — до 1.618 = ${priceText(fiboLevel(grid, 1.618))}.`,
                });
            }
            if (flag(p, 'notifyBack', false) && grid.reverse === 'back' && grid.reverseAt === last.time) {
                out.push({
                    key: `${id}-back`,
                    side: up ? 'SHORT' : 'LONG',
                    text: `От 1.414 цена вернулась к 1.0 (${priceText(fiboLevel(grid, 1))}) — разворот от зоны сработал.`,
                });
            }
        }
        return out;
    },
};
/** Индикаторы с оповещениями: ключ — `id` индикатора в терминале. */
/**
 * «Выгодные зоны PRO 2»: вход после снятия ликвидности у зоны.
 *
 * Тот же расчёт, что рисует отметку на графике (`pro2State`), по тем же
 * настройкам подписки. Событие — на свече входа; по желанию — ещё на свече
 * снятия, прошедшей условия: «сняли, ждём слом».
 */
const ZONES_PRO_2 = {
    name: 'Выгодные зоны PRO 2',
    about: 'вход после снятия ликвидности у зоны — со стопом и целью',
    frames: none,
    data: (p) => (flag(p, 'flush', false) ? ['futuresBuyShare', 'openInterest'] : ['futuresBuyShare']),
    events({ bars, series }, p) {
        const last = bars[bars.length - 1];
        if (!last || bars.length < 700)
            return [];
        const options = pro2OptionsFrom(p);
        const state = pro2State({
            candles: bars,
            zones: zoneSeries(bars),
            levels: liquidityMap(bars, { levels: num(p, 'levels', 400), age: num(p, 'age', 2500) }),
            buyShare: series?.futuresBuyShare ?? [],
            openInterest: series?.openInterest ?? [],
            livePartial: false,
        }, options);
        const out = [];
        for (const mark of state.marks) {
            if (mark.time !== last.time)
                continue;
            const share = mark.share === null ? '' : `, ${mark.long ? 'покупки' : 'продажи'} по рынку ${Math.round((mark.long ? mark.share : 1 - mark.share) * 100)} %`;
            out.push({
                key: `pro2-${mark.time}-${mark.long ? 'long' : 'short'}`,
                side: mark.long ? 'LONG' : 'SHORT',
                text: `PRO 2 — вход в ${mark.long ? 'лонг' : 'шорт'} по ${priceText(mark.entry)}: в зоне сняли уровень ${priceText(mark.sweepPrice)}${share}, слом подтверждён.${pro2PlanShown(p) ? ` Стоп ${priceText(mark.stop)}, цель ${priceText(mark.target)} (1:${options.target}).` : ''}`,
            });
        }
        if (flag(p, 'notifySweep', false)) {
            for (const wait of state.waiting) {
                if (wait.sweepTime !== last.time || !wait.passes)
                    continue;
                out.push({
                    key: `pro2-sweep-${wait.sweepTime}-${wait.long ? 'long' : 'short'}`,
                    side: null,
                    text: `PRO 2 — в зоне сняли уровень, ждём слом: закрытие ${wait.long ? 'выше' : 'ниже'} ${priceText(wait.trigger)} даст вход в ${wait.long ? 'лонг' : 'шорт'}, ${wait.long ? 'ниже' : 'выше'} ${priceText(wait.extreme)} — отмена.`,
                });
            }
        }
        return out;
    },
};
export const INDICATOR_EVENTS = {
    imbalances: IMBALANCES,
    'order-blocks': ORDER_BLOCKS,
    sma: average('SMA', 'sma', 20),
    ema: average('EMA', 'ema', 21),
    ma3: TRIPLE,
    bb: BOLLINGER,
    rsi: RSI,
    macd: MACD,
    'liquidity-map': LIQUIDITY,
    'volume-profile': PROFILE,
    'spot-cvd': cvd('Spot CVD', 'spotDelta'),
    'futures-cvd': cvd('Futures CVD', 'futuresDelta'),
    'order-flow-delta': ORDER_FLOW_DELTA,
    'market-liquidations': LIQUIDATIONS,
    'crowd-positioning': CROWD,
    sessions: SESSIONS,
    'fibo-check': FIBO_CHECK,
    'zones-pro-2': ZONES_PRO_2,
};
//# sourceMappingURL=indicator-watch.js.map